import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MyShareItem } from '@reqshare/shared';
import { useWebAuth } from '../../hooks/useWebAuth';
import { getMyShares } from '../../api/my-shares';
import { deleteRequest, deleteBatchRequest } from '../../api/get-request';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import { getGoogleAuthUrl, getGithubAuthUrl } from '../../api/auth-api';
import './SharedRequestsPage.scss';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/shareqsite/oloccejiekekblplkinfljiogebnhoao';

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function formatExpires(iso: string | null): string {
  if (iso === null) return 'Never expires';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `Expires in ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `Expires in ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `Expires in ${hr}h`;
  const day = Math.floor(hr / 24);
  return `Expires in ${day}d`;
}

export function SharedRequestsPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useWebAuth();

  const [items, setItems] = useState<MyShareItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MyShareItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getMyShares(token)
      .then(setItems)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [authLoading, isAuthenticated, token]);

  const handleRowClick = (item: MyShareItem) => {
    navigate(item.kind === 'single' ? `/r/${item.id}` : `/b/${item.id}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, item: MyShareItem) => {
    e.stopPropagation();
    setDeleteError(null);
    setPendingDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete || !token) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (pendingDelete.kind === 'single') {
        await deleteRequest(pendingDelete.id, token);
      } else {
        await deleteBatchRequest(pendingDelete.id, token);
      }
      setItems((prev) => prev.filter((i) => !(i.id === pendingDelete.id && i.kind === pendingDelete.kind)));
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="shared-requests-page">
      <div className="shared-requests-page__container">
        <header className="shared-requests-page__header">
          <h1 className="shared-requests-page__title">Shared requests</h1>
          <p className="shared-requests-page__subtitle">
            All the requests you've shared that haven't expired yet.
          </p>
        </header>

        <div className="shared-requests-page__card">
          {authLoading || loading ? (
            <div className="shared-requests-page__state">Loading shared requests…</div>
          ) : !isAuthenticated ? (
            <AuthBlock />
          ) : error ? (
            <div className="shared-requests-page__state shared-requests-page__error">{error}</div>
          ) : items.length === 0 ? (
            <div className="shared-requests-page__state">
              No shared requests yet.
              <br />
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="shared-requests-page__empty-cta"
              >
                Install the extension to start sharing
              </a>
            </div>
          ) : (
            <div className="shared-requests-page__list">
              {items.map((item) => (
                <ShareRow
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  onClick={() => handleRowClick(item)}
                  onDelete={(e) => handleDeleteClick(e, item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {pendingDelete && (
        <ConfirmModal
          title="Delete shared request"
          confirmLabel="Delete"
          cancelLabel="Keep"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (!deleting) {
              setPendingDelete(null);
              setDeleteError(null);
            }
          }}
          loading={deleting}
        >
          <p>This cannot be undone. Anyone with the link will no longer be able to view it.</p>
          {deleteError && <p className="shared-requests-page__error">{deleteError}</p>}
        </ConfirmModal>
      )}
    </main>
  );
}

interface ShareRowProps {
  item: MyShareItem;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

function ShareRow({ item, onClick, onDelete }: ShareRowProps) {
  const methodClass =
    item.kind === 'batch'
      ? 'shared-requests-page__method--batch'
      : `shared-requests-page__method--${item.method.toLowerCase()}`;
  const label = item.kind === 'batch' ? 'BATCH' : item.method;
  const title = item.kind === 'batch' ? `Batch · ${item.entryCount} requests` : item.url;

  return (
    <button
      type="button"
      className="shared-requests-page__row"
      onClick={onClick}
    >
      <div className="shared-requests-page__row-main">
        <div className="shared-requests-page__row-top">
          <span className={`shared-requests-page__method ${methodClass}`}>{label}</span>
          <span className="shared-requests-page__url">{title}</span>
        </div>
        <div className="shared-requests-page__meta">
          Created {formatRelative(item.createdAt)} · {formatExpires(item.expiresAt)}
        </div>
      </div>
      <span
        role="button"
        tabIndex={0}
        aria-label="Delete shared request"
        className="shared-requests-page__delete"
        onClick={onDelete}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onDelete(e as unknown as React.MouseEvent);
          }
        }}
      >
        Delete
      </span>
    </button>
  );
}

function AuthBlock() {
  return (
    <div className="shared-requests-page__auth-card">
      <p>Sign in to see your shared requests.</p>
      <a className="shared-requests-page__empty-cta" href={getGoogleAuthUrl()}>
        Sign in with Google
      </a>
      <a className="shared-requests-page__empty-cta" href={getGithubAuthUrl()}>
        Sign in with GitHub
      </a>
    </div>
  );
}
